import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import * as THREE from 'three';
import FVector from '@data/geometry/FVector';
import type { FVector as IFVector } from '@visualizer/types';

export type CameraSettings = {
    enablePan: boolean;
    enableZoom: boolean;
    minPolarAngle: number;
    maxPolarAngle: number;
    minAzimuthAngle: number;
    maxAzimuthAngle: number;
    trackBallZoom: boolean;
};

abstract class CustomCamera {
    // Camera controls
    protected camera: THREE.Camera;

    protected orbitControls: OrbitControls;
    protected trackballControls: TrackballControls;

    // Orbit settings
    protected settings: CameraSettings = this.defaultSettings();

    constructor(camera: THREE.Camera, orbitControls: OrbitControls, trackballControls: TrackballControls) {
        this.camera = camera;
        this.orbitControls = orbitControls;
        this.trackballControls = trackballControls;
    }

    public getCamera(): THREE.Camera {
        return this.camera;
    }

    public setPositionAndTarget(position: IFVector, target: IFVector): void {
        const { x: x_position, y: y_position, z: z_position } = position;
        const { x: x_target, y: y_target, z: z_target } = target;

        this.camera.position.set(x_position, y_position, z_position);
        this.orbitControls.target.set(x_target, y_target, z_target);

        this.orbitControls.update();
    }
    public getPositionAndTarget(): { position: FVector; target: FVector } {
        const position = FVector.fromThree(this.camera.position);
        const target = FVector.fromThree(this.orbitControls.target);
        return { position: position, target: target };
    }

    public activate(position: IFVector, target: IFVector): void {
        this.updateControlsSettings();
        this.setPositionAndTarget(position, target);
    }

    public deActivate(): { position: FVector; target: FVector } {
        return this.getPositionAndTarget();
    }

    protected defaultSettings(): CameraSettings {
        return {
            enablePan: true,
            enableZoom: true,
            minPolarAngle: 0,
            maxPolarAngle: Math.PI,
            minAzimuthAngle: -Infinity,
            maxAzimuthAngle: Infinity,
            trackBallZoom: true,
        };
    }

    protected updateControlsSettings(): void {
        this.orbitControls.object = this.camera;

        this.orbitControls.enablePan = this.settings.enablePan;
        this.orbitControls.enableZoom = this.settings.enableZoom;
        this.orbitControls.minPolarAngle = this.settings.minPolarAngle;
        this.orbitControls.maxPolarAngle = this.settings.maxPolarAngle;
        this.orbitControls.minAzimuthAngle = this.settings.minAzimuthAngle;
        this.orbitControls.maxAzimuthAngle = this.settings.maxAzimuthAngle;

        this.trackballControls.noZoom = !this.settings.trackBallZoom;
    }
}

export default CustomCamera;
